/**
 * Dateien vom Rechner — aus dem Explorer gezogen oder über „Dateien
 * auswählen…" / „Ordner auswählen…" geholt.
 *
 * ══ WARUM KEINE ORDNER-LISTE MEHR (PO 28.09.2026) ═════════════════════════
 * #1779 zeigte freigegebene Ordner (File System Access API) direkt als Liste.
 * Die Freigabe lässt sich aber nicht pauschal erteilen: jeder Nutzer muss
 * jeden Ordner selbst freigeben, und Edge fragt pro Sitzung erneut. Deshalb
 * wieder der klassische Weg — Ziehen oder Explorer —, nur dass gezogene
 * ORDNER jetzt samt Unterordnern ankommen.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type RechnerDatei = {
    datei: File;
    /** Ordnerpfad relativ zum gezogenen/gewählten Ordner; "" = lose Datei. */
    ordner: string;
};

/** Versteckte und Systemdateien will niemand anhängen. */
export const istSystemdatei = (name: string): boolean => {
    const n = name.toLowerCase();
    return n.startsWith(".") || n === "desktop.ini" || n === "thumbs.db";
};

/** Ordner eines relativen Pfads („Scans/2026/a.pdf" → „Scans/2026"). */
export const ordnerVon = (relativerPfad: string): string => {
    const strich = relativerPfad.lastIndexOf("/");
    return strich > 0 ? relativerPfad.slice(0, strich) : "";
};

/**
 * Aus einem `<input type="file">` — mit `webkitdirectory` tragen die Dateien
 * ihren Pfad in `webkitRelativePath`.
 */
export const ausDateiFeld = (dateien: readonly File[]): RechnerDatei[] =>
    dateien
        .filter((d) => !istSystemdatei(d.name))
        .map((datei) => ({ datei, ordner: ordnerVon(datei.webkitRelativePath ?? "") }));

/**
 * Das Nötigste der Entry-API (`webkitGetAsEntry`), selbst typisiert — die
 * Typen in `lib.dom` sind je nach TypeScript-Version unvollständig.
 */
type Eintrag = {
    isFile: boolean;
    isDirectory: boolean;
    name: string;
    fullPath: string;
    file?: (ok: (f: File) => void, fehler: (e: unknown) => void) => void;
    createReader?: () => {
        readEntries: (ok: (e: Eintrag[]) => void, fehler: (e: unknown) => void) => void;
    };
};

const leseDatei = (e: Eintrag): Promise<File | null> =>
    new Promise((resolve) => (e.file ? e.file(resolve, () => resolve(null)) : resolve(null)));

/** `readEntries` liefert in Häppchen (Chromium: je 100) — bis es leer zurückkommt. */
const leseOrdner = async (e: Eintrag): Promise<Eintrag[]> => {
    const leser = e.createReader?.();
    if (!leser) return [];
    const alle: Eintrag[] = [];
    for (;;) {
        const teil = await new Promise<Eintrag[]>((resolve) => leser.readEntries(resolve, () => resolve([])));
        if (teil.length === 0) return alle;
        alle.push(...teil);
    }
};

/** Obergrenze je Ablage — wer versehentlich „C:\Projekte" zieht, soll nicht warten. */
export const MAX_DATEIEN = 500;

/**
 * Was beim Loslassen im Dialog landet — Dateien und ganze Ordner.
 *
 * Die Einträge müssen SYNCHRON im Drop-Handler geholt werden: danach ist
 * `dataTransfer.items` leer. Deshalb nimmt die Funktion die Liste, nicht das
 * Ereignis. Ohne Entry-API (sehr alte Browser) bleibt `dataTransfer.files`.
 */
export const eintraegeAusDrop = (dt: DataTransfer): Eintrag[] | null => {
    const items = Array.from(dt.items ?? []);
    if (items.length === 0 || typeof (items[0] as unknown as { webkitGetAsEntry?: unknown }).webkitGetAsEntry !== "function") {
        return null;
    }
    return items
        .filter((i) => i.kind === "file")
        .map((i) => (i as unknown as { webkitGetAsEntry: () => Eintrag | null }).webkitGetAsEntry())
        .filter((e): e is Eintrag => e != null);
};

export const ausDrop = async (eintraege: Eintrag[]): Promise<RechnerDatei[]> => {
    const ergebnis: RechnerDatei[] = [];
    const besuche = async (e: Eintrag) => {
        if (ergebnis.length >= MAX_DATEIEN || istSystemdatei(e.name)) return;
        if (e.isFile) {
            const datei = await leseDatei(e);
            // fullPath beginnt mit „/" — ohne ihn ist er relativ zur Ablage.
            if (datei) ergebnis.push({ datei, ordner: ordnerVon(e.fullPath.replace(/^\//, "")) });
            return;
        }
        if (e.isDirectory) {
            for (const kind of await leseOrdner(e)) await besuche(kind);
        }
    };
    for (const e of eintraege) await besuche(e);
    return ergebnis;
};
